import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from '../store';
import { updateError } from './user';
import { getPracticeId } from './clincheck';

const initialState = {
    wpNotifications: [],
    emailNotifications: [],
    notificationsJwt: '',
    // The per-user task room and a token minted FOR that room. Kept separate
    // from notificationsJwt on purpose: these are two rooms, and a PieSocket
    // token authorises exactly one. Subscribing the notifications socket to
    // the task room is what produced the 23 Sep reconnect storm. Empty when
    // the backend issues no task credential — the handler then opens no task
    // room at all, which is the safe shape: a missing token means one fewer
    // room, a wrong token means an infinite retry loop.
    tasksJwt: '',
    tasksRoom: '',
    // The practice the notifications token above was minted for, recorded at
    // fetch time. The token is scoped to `${practice}_notifications` and the
    // practice on screen can change before a refetch lands, so the handler
    // needs to be able to tell "this token is for somewhere else" from "this
    // token is fine". null means not reported — the handler then behaves as it
    // did before this field existed.
    notificationsPractice: null,
    // Bumped when a persisted row changes state in place (inbound call ringing -> answered/missed)
    // so the bell re-fetches its list; the array length does not change for an update.
    refreshTick: 0,
};

const slice = createSlice({
    name: 'liveNotifications',
    initialState,
    reducers: {
        resetWpNotifications(state) {
            Object.assign(state, initialState);
        },
        setPersistedWpNotifications(state, action) {
            const notification = action.payload;
            // Ensure notification has a unique ID for close functionality
            if (!notification.id) {
                notification.id = `wp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            // Only add if notification with same ID doesn't already exist
            const exists = state.wpNotifications.some((n) => n.id === notification.id);
            if (!exists) {
                state.wpNotifications = [...state.wpNotifications, notification];
            }
        },
        setPersistedEmailNotifications(state, action) {
            const notification = action.payload;
            // Ensure notification has a unique ID for close functionality
            if (!notification.id) {
                notification.id = `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            // Only add if notification with same ID doesn't already exist
            const exists = state.emailNotifications.some((n) => n.id === notification.id);
            if (!exists) {
                state.emailNotifications = [...state.emailNotifications, notification];
            }
        },
        // Live-update mutation for the same persisted-email-notifications
        // list. Used when the backend pushes a 'notification-updated'
        // PieSocket event — e.g. when a grouped click-tracking
        // notification's grouped_count gets bumped on every subsequent
        // click within the same day. We find the row by id and patch
        // description + grouped_count in place; no new toast, no array
        // append (which would create a phantom duplicate).
        updatePersistedEmailNotification(state, action) {
            const patch = action.payload;
            if (!patch || !patch.id) return;
            const idx = state.emailNotifications.findIndex((n) => n.id === patch.id);
            if (idx === -1) return;
            state.emailNotifications[idx] = {
                ...state.emailNotifications[idx],
                ...patch,
            };
        },
        bumpNotificationsRefresh(state) {
            state.refreshTick = (state.refreshTick || 0) + 1;
        },
        setWpNotifications(state, action) {
            state.wpNotifications = action.payload;
        },
        // Payload is { auth, practiceId }: the credential and the practice it
        // belongs to, handed over together. Same reason setTasksCredential
        // carries its room — a credential that does not say what it is for
        // leaves the browser guessing, and the browser guessed wrong on 23 Sep.
        setNotificationsJwt(state, action) {
            state.notificationsJwt = action.payload?.auth ?? '';
            state.notificationsPractice = action.payload?.practiceId ?? null;
        },
        setTasksCredential(state, action) {
            state.tasksJwt = action.payload?.auth ?? '';
            state.tasksRoom = action.payload?.room ?? '';
        },
        setEmailNotifications(state, action) {
            state.emailNotifications = action.payload;
        },
    },
});

export default slice.reducer;

export const { setWpNotifications, resetWpNotifications, setPersistedWpNotifications, setEmailNotifications, setPersistedEmailNotifications, updatePersistedEmailNotification, bumpNotificationsRefresh, setTasksCredential } = slice.actions;

// ── Shared WhatsApp toast dedup set ──
// The platform channel and the phone-specific WhatsApp channel can both carry
// realtime WhatsApp events. These sets keep cross-channel retries from showing
// duplicate toasts in the same browser session.
export const shownWpToastIds = new Set();
export const shownWhatsappCallToastIds = new Set();

// ── General verb-based toast dedup set ──
// Prevents duplicate toasts when both frontend detection and backend PieSocket
// fire for the same event (e.g. follow-up table move).  Entries auto-expire
// via setTimeout so the Set stays small.
export const recentToastKeys = new Set();

// ── Notification ID dedup set ──
// Tracks notification IDs (any verb) that have already been shown as an
// in-app toast in the current session.  Auto-expires after 30s so the set
// stays small.  Used to prevent duplicates when:
//   • PieSocket delivers the same event twice (network retry / reconnect)
//   • Firebase foreground onMessage fires for the same event as PieSocket
export const shownNotifIds = new Set();

// ── Single-flight for notifications websocket JWT ──
// Production traces show this POST fires twice per load: the LiveNotificationHandler
// useEffect dep array [isAuthenticated, user] fires first when isAuthenticated becomes
// true, then again when the user object reference updates (a common pattern in the
// auth slice). Both calls hit before PieSocket is created, so the pieSocket.current
// null-guard in the component does not prevent the duplicate JWT fetch.
//
// Fix: module-level inflight record keyed by `${practiceId}:${userId}`.
// Concurrent AND staggered callers within the same load share one promise.
// A new key (different practice or user) starts a fresh fetch.
// Rejection clears the cache entry so the next call retries (failed JWT is retryable).
//
// Mirrors the pattern in src/utils/authRefresh.js.
let _jwtInflight = { key: null, promise: null };

export function getNotificationsJwt(data) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const { userId } = data;
            const inflightKey = `${practiceId}:${userId}`;

            // Return cached in-flight promise if key matches (same practice + user)
            if (_jwtInflight.key === inflightKey && _jwtInflight.promise) {
                return _jwtInflight.promise;
            }

            const doFetch = async () => {
                const response = await callApi(`/api/v1/practices/${practiceId}/users/${userId}/notifications/generate_websocket_jwt/`, 'post');
                if (response.status === 200) {
                    dispatch(slice.actions.setNotificationsJwt({ auth: response.data.auth, practiceId }));
                    // Absent on older backends and for users with no usable
                    // PracticeUser row. Dispatched either way so a practice
                    // switch clears a stale room/token pair rather than
                    // carrying the previous practice's into the new one.
                    dispatch(slice.actions.setTasksCredential(response.data.tasks_auth));
                } else {
                    dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
                }
            };

            _jwtInflight = {
                key: inflightKey,
                promise: doFetch().catch((error) => {
                    // Expire on rejection so the next dispatch retries.
                    // On success we intentionally keep the cached promise so that
                    // repeat callers within the same load (e.g. a second effect fire
                    // caused by a user-object reference update) reuse the settled result
                    // instead of issuing a new POST.
                    if (_jwtInflight.key === inflightKey) {
                        _jwtInflight = { key: null, promise: null };
                    }
                    dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
                }),
            };

            return _jwtInflight.promise;
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// Exported for testing only — allows resetting the inflight state between tests
export const __notifJwtTestUtils = {
    resetInflight: () => { _jwtInflight = { key: null, promise: null }; },
};
